/**
 * Detect Supabase email-link flows on `/auth` (invite vs password recovery).
 *
 * Three detection methods:
 * 1. Hash fragments set by GoTrue after implicit-flow redirect (`#access_token=...&type=recovery`)
 * 2. Query params from a custom email template link (`?token_hash=...&type=recovery`)
 * 3. Query `flow=invite` set by our API redirectTo
 */

export type CredentialSetupKind = 'recovery' | 'invite';

export function readCredentialSetupKindFromBrowser(): CredentialSetupKind | null {
  if (typeof window === 'undefined') return null;

  // Implicit flow: GoTrue redirects to app with tokens in hash fragment
  const h = window.location.hash;
  if (h.includes('type=recovery')) return 'recovery';
  if (h.includes('type=invite')) return 'invite';

  // PKCE / custom-template flow: email link has token_hash + type as query params
  const sp = new URLSearchParams(window.location.search);
  const tokenHash = sp.get('token_hash');
  const tokenType = sp.get('type');
  if (tokenHash && tokenType === 'recovery') return 'recovery';
  if (tokenHash && tokenType === 'invite') return 'invite';

  // Invite flow from our API redirectTo
  if (sp.get('flow') === 'invite') return 'invite';

  return null;
}

/**
 * Extract `token_hash` and `type` from query params when present (custom email template flow).
 * Returns null if the URL uses the implicit hash-fragment flow instead.
 */
export function readTokenHashFromBrowser(): { token_hash: string; type: string } | null {
  if (typeof window === 'undefined') return null;
  const sp = new URLSearchParams(window.location.search);
  const tokenHash = sp.get('token_hash');
  const type = sp.get('type');
  if (tokenHash && type) return { token_hash: tokenHash, type };
  return null;
}
